/**
 * @note Intention here is to fairly drastically simplify the data model/schemas
 *       around predictions, as the naming got out of hand, and things were left too flexible.
 *       --
 *       @todo: Use this to build tests, and then fix actual implementation that omits rejected intervals
 */

import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

import {
  DateTimeTzSchema,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GenericUUID,
  LuxonDateTimeSchema,
  VariousMemoryIdRefsSchema,
  YYYYMMDDSchema,
} from '../models.generic';
import { AiPredictionAsDatesUpdatedAtSchema } from './models.ai_engine';
import { AiStatusLogSchema } from './models.ai_prediction_logs';

/** `flagged_as` enums */
export type PredictionFlaggedAs = z.infer<typeof PredictionFlaggedAsSchema>;
const PredictionFlaggedAsEnums = ['rejected', 'note_regenerated'] as const;
export const PredictionFlaggedAsSchema = z.enum(PredictionFlaggedAsEnums);

/**
 * Infered data from AI predictions
 */
export type AiInference = z.infer<typeof AiInferenceSchema>;
export const AiInferenceSchema = z.object({
  confidence: z
    .number()
    .describe('Confidence of prediction')
    .min(0)
    .max(1)
    .optional()
    .openapi({ example: 0.9 }),
  note: z.string().max(2048).optional().nullable().openapi({
    description: 'Suggested note to use for time entry',
    example: 'Meeting to discuss project X',
  }),
  valid_as_hour: z.boolean().optional().default(false).openapi({
    description: 'If true, this prediction has all the required data to create a time entry.',
    example: true,
  }),
  timely_project_id: z
    .number()
    .describe('The Timely `project.id` most likely to apply')
    .int()
    .openapi({ example: 1234 }),
  memory_ids: z
    .array(VariousMemoryIdRefsSchema)
    .openapi({
      example: ['1234', '5678'],
      description: 'Memory IDs that are most likely to apply to this prediction',
    })
    .default([])
    .optional(),
  timely_label_ids: z
    .array(z.number().describe('The Timely `label.id`s most likely to apply').int())
    .openapi({
      example: [32, 11],
      description:
        'One ID (if only predicting parent tag), or two IDs if predicting to a child label_id',
    })
    .default([])
    .optional(),
});

/**
 *
 * Base schema for AI predictions
 * @todo `inferred`/migration/db schema
 */
export type AiPredictionBase = z.infer<typeof AiPredictionBaseSchema>;
export const AiPredictionBaseSchema = z.object({
  id: GenericUUID.optional(),
  started_at: DateTimeTzSchema,
  ended_at: DateTimeTzSchema,
  inferred: AiInferenceSchema,
  flagged_as: PredictionFlaggedAsSchema.or(z.string())
    .optional()
    .nullable()
    .default(null)
    .describe('Allows string, but should be one of the enums'),
  origin_prediction_id: GenericUUID.describe('The original prediction ID we flagged')
    .optional()
    .nullable()
    .default(null),
  timely_user_id: GenericTimelyUserId,
  timely_account_id: GenericTimelyAccountId,
  user_id: GenericUUID,
});

/**
 * Read Prediction from DB
 */
export type AiPredictionRead = z.infer<typeof AiPredictionReadSchema>;
export const AiPredictionReadSchema = AiPredictionBaseSchema.required({ id: true }).extend({
  created_at: DateTimeTzSchema,
});

/**
 * Write Prediction to DB
 */
export type AiPredictionWrite = z.infer<typeof AiPredictionWriteSchema>;
export const AiPredictionWriteSchema = AiPredictionBaseSchema.pick({
  user_id: true,
  origin_prediction_id: true,
  flagged_as: true,
});

/**
 * Reject/"Delete" Prediction in DB
 * aka flagged_as: 'rejected'
 */
export type AiPredictionDelete = z.infer<typeof AiPredictionDeleteSchema>;
export const AiPredictionDeleteSchema = AiPredictionBaseSchema.pick({
  id: true,
  // flagged_as: true, <- not required, as we default to 'rejected'
  started_at: true,
  ended_at: true,
}).required();

/**
 * Presentable Predictions in API
 */

export type PresentableAiPrediction = z.infer<typeof PresentableAiPredictionSchema>;
export const PresentableAiPredictionSchema = AiPredictionBaseSchema.pick({
  id: true,
  started_at: true,
  ended_at: true,
  inferred: true,
  flagged_as: true,
});

/**
 * Query params for AI status
 */
export type QueryAiPredictionStatusForDateRange = z.infer<
  typeof QueryAiPredictionStatusForDateRangeSchema
>;
export const QueryAiPredictionStatusForDateRangeSchema = z.object({
  from_date: YYYYMMDDSchema,
  to_date: YYYYMMDDSchema,
  timely_user_id: GenericTimelyUserId,
  timely_account_id: GenericTimelyAccountId,
});

/**
 * AI status response
 */
export type GetAiPredictionStatusForDateRangeBody = z.infer<
  typeof GetAiPredictionStatusForDateRangeBodySchema
>;
export const GetAiPredictionStatusForDateRangeBodySchema = z.object({
  status: z.array(AiStatusLogSchema),
  dates_updated_at: AiPredictionAsDatesUpdatedAtSchema,
});

/**
 * Query Predictions in Date Range
 * @see getAiPredictionsInDateRange operationId/API
 * @see getAiPredictionsInDateRange.ts
 */
export type QueryPredictionsInDateRange = z.infer<typeof QueryPredictionsInDateRangeSchema>;
export const QueryPredictionsInDateRangeSchema = AiPredictionBaseSchema.pick({
  started_at: true,
  ended_at: true,
  timely_account_id: true,
  user_id: true,
})
  .required()
  .extend({
    apply_rejections: z.boolean().optional().default(true).openapi({
      description:
        'If true (default), will omit rejected intervals from predictions. If **false**, "negative predictions" are also returned; may be useful for client-side to make optimistic updates easier.',
      example: true,
    }),
  });

/**
 * Response to Refreshing Predictions in Date Range
 */
export type RefreshPredictionsInDateRangeBody = z.infer<
  typeof RefreshPredictionsInDateRangeBodySchema
>;
export const RefreshPredictionsInDateRangeBodySchema = z.object({
  timely_account_id: GenericTimelyAccountId,
  timely_user_id: GenericTimelyUserId,
  from_date: YYYYMMDDSchema.describe('YYYY-MM-DD relative to the users Timely specified timezone'),
  to_date: YYYYMMDDSchema.describe('YYYY-MM-DD relative to the users Timely specified timezone'),
  consequential_change_at: DateTimeTzSchema.optional().describe(
    'Guides AI dedupe logic. Most recent value of either `ai_preferences.updated_at` OR whenever last inference was completed in the supplied `from/to_date` range'
  ),
  timely_recent_project_ids: z
    .array(z.number().int())
    .optional()
    .describe('Project IDs to use for dev-env fake suggestions')
    .openapi({ example: [1, 2, 3] }),
});

/**
 *
 */
export type MetaForPredictionsInDateRange = z.infer<typeof MetaForPredictionsInDateRangeSchema>;
export const MetaForPredictionsInDateRangeSchema = z.object({
  count: z.number().int(),
  totalCount: z.number().int(),
  dates_updated_at: z.record(z.string().datetime({ offset: true }).nullable()),
});

/**
 * Reject/delete (parts of) a time interval prediction
 */
export type RejectAiPredictionWrite = z.infer<typeof RejectAiPredictionWriteSchema>;
export const RejectAiPredictionWriteSchema = AiPredictionBaseSchema.pick({
  id: true,
  started_at: true,
  ended_at: true,
}).required();

/**
 * TimelyPresentableAiPrediction
 * For centralization reasons, we're defining Timely's schema here
 */

export type TimelyPresentableAiPrediction = z.infer<typeof TimelyPresentableAiPredictionSchema>;
export const TimelyPresentableAiPredictionSchema = z.object({
  note: z.string().optional().nullable(),
  id: GenericUUID,
  date: YYYYMMDDSchema,
  /**
   * Timely's all client-side, and since we can't construct this full payload on TIC side anyway,
   * it's probably fine to use LuxonDateTimeSchema's here?
   */
  started_at: LuxonDateTimeSchema,
  ended_at: LuxonDateTimeSchema,
  confidence: z.number(),
  color: z.string(),
  existing_hour_id: z.number().optional().nullable(),
  total_seconds: z.number(),
  valid_as_hour: z.boolean(),
  project_id: z.number().optional().nullable(),
  label_ids: z.array(z.number()),
  memory_ids: z.array(VariousMemoryIdRefsSchema),
  labels: z.array(z.object({ id: z.number(), name: z.string(), emoji: z.string().optional() })),
  collapsed: z.boolean(),
  /** Was originally typed as  LightProject | Project, probably not needed */
  project: z
    .object({
      id: z.number(),
      name: z.string(),
      color: z.string().optional().nullable(),
      client: z
        .object({
          id: z.number(),
          name: z.string(),
          color: z.string().optional().nullable(),
        })
        .optional(),
    })
    // .passthrough() // <- if we want things from LightProject | Project
    .optional()
    .nullable(),
  timestamps: z.array(
    z.object({
      started_at: LuxonDateTimeSchema,
      ended_at: LuxonDateTimeSchema,
    })
  ),
});
